// src/contexts/UserContext.jsx - EXTREMELY ROBUST LOADING AND DEBUGGING
import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuest } from './GuestContext';
import firebaseInstance from '../services/firebaseSingleton';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { setUpGuestMode } from '../services/auth';
import { setAuthToken, removeAuthToken } from '../services/api';

const AUTH_READY_TIMEOUT_MS = 15000; // 15 seconds for Firebase auth to give an initial state

const UserContext = createContext({
  user: null,
  setUser: () => console.warn("setUser called on default UserContext"),
  login: async () => console.warn("login called on default UserContext"),
  logout: async () => console.warn("logout called on default UserContext"),
  error: null,
  loading: true, 
});

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const { setGuestMode: setGlobalGuestMode } = useGuest();
  const [user, setUserState] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); 
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const authTimeoutIdRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    console.log('[UserContext] Component Mounted');
    return () => {
      mountedRef.current = false;
      if (authTimeoutIdRef.current) {
        clearTimeout(authTimeoutIdRef.current);
      }
      console.log('[UserContext] Component Unmounted, cleared auth timeout if any.');
    };
  }, []);

  useEffect(() => {
    console.log('[UserContext] Auth Listener Effect: START. Current isAuthLoading:', isAuthLoading);

    // If this effect re-runs for some reason and loading was already false,
    // set it back to true as we are re-evaluating auth state.
    if (mountedRef.current && !isAuthLoading) {
      console.log('[UserContext] Auth Listener Effect: Re-running, setting isAuthLoading to true.');
      setIsAuthLoading(true);
    }

    authTimeoutIdRef.current = setTimeout(() => {
      if (mountedRef.current && isAuthLoading) { // Check isAuthLoading again
        console.error(`[UserContext] AUTH TIMEOUT after ${AUTH_READY_TIMEOUT_MS}ms! Firebase onAuthStateChanged did not fire or resolve in time. Forcing loading state to false.`);
        setAuthError('Authentication status check timed out. Please refresh the page.');
        setIsAuthLoading(false);
      }
    }, AUTH_READY_TIMEOUT_MS);

    if (!firebaseInstance.isInitialized()) {
      console.warn('[UserContext] Firebase not initialized when auth listener effect attempted to run. Setting auth loading to false.');
      if (mountedRef.current) setIsAuthLoading(false);
      clearTimeout(authTimeoutIdRef.current);
      return () => {};
    }

    let auth;
    try {
      auth = firebaseInstance.getAuthInstance();
      if (!auth) {
        throw new Error("Failed to get Firebase Auth instance.");
      }
    } catch (error) {
        console.error('[UserContext] Error getting Firebase Auth instance:', error);
        if (mountedRef.current) {
            setAuthError('Firebase Auth service unavailable.');
            setIsAuthLoading(false);
        }
        clearTimeout(authTimeoutIdRef.current);
        return () => {};
    }
    
    console.log('[UserContext] Attaching onAuthStateChanged listener...');
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(authTimeoutIdRef.current); // Auth state received, clear timeout

      if (!mountedRef.current) {
        console.log('[UserContext] onAuthStateChanged: Component unmounted during callback. Aborting state update.');
        return;
      }
      console.log('[UserContext] onAuthStateChanged: Callback FIRED. Firebase user UID:', firebaseUser?.uid || 'null');

      try {
        if (firebaseUser) {
          console.log('[UserContext] onAuthStateChanged: Authenticated user found. Processing...');
          const idToken = await firebaseUser.getIdToken();
          if (!mountedRef.current) return; // Check again after await

          setAuthToken(idToken);
          setUserState({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isGuest: false,
            isAuthenticated: true,
          });
          setAuthError(null);
          console.log('[UserContext] onAuthStateChanged: User state SET for authenticated user.');
        } else {
          console.log('[UserContext] onAuthStateChanged: No authenticated user from Firebase. Checking for guest...');
          removeAuthToken();
          const guestUserDataStorage = localStorage.getItem('guestUser');
          let guestUserSession = null;
          if (guestUserDataStorage) {
              try { guestUserSession = JSON.parse(guestUserDataStorage); } catch(e) { localStorage.removeItem('guestUser'); console.error("[UserContext] Error parsing guest user from LS", e); }
          }
          setUserState(guestUserSession);
          setAuthError(null);
          console.log('[UserContext] onAuthStateChanged: User state SET for null/guest user.');
        }
      } catch (err) {
        console.error('[UserContext] onAuthStateChanged: Error processing user state:', err);
        if (mountedRef.current) {
          removeAuthToken();
          setUserState(null);
          setAuthError('Failed to process user session details.');
        }
      } finally {
        if (mountedRef.current) {
          console.log('[UserContext] onAuthStateChanged: FINALLY - Setting isAuthLoading to FALSE.');
          setIsAuthLoading(false);
        }
      }
    }, (error) => { 
        clearTimeout(authTimeoutIdRef.current); // Auth listener error, clear timeout
        console.error('[UserContext] CRITICAL onAuthStateChanged listener itself errored:', error);
        if (mountedRef.current) {
            removeAuthToken();
            setUserState(null);
            setAuthError('Critical authentication system listener error.');
            console.log('[UserContext] Setting isAuthLoading to FALSE due to listener error.');
            setIsAuthLoading(false);
        }
    });
    
    return () => {
      console.log('[UserContext] Cleanup: Unsubscribing from onAuthStateChanged listener and clearing timeout.');
      unsubscribe();
      if (authTimeoutIdRef.current) clearTimeout(authTimeoutIdRef.current);
    };
  }, []); // Empty dependency array: runs ONCE on mount.

  const login = useCallback(async (platform) => {
    // ... (login logic, ensure setIsAuthLoading is handled if it performs async auth work NOT covered by onAuthStateChanged immediately)
    // For OAuth, this function mainly just triggers a redirect. The actual user state update comes via onAuthStateChanged.
    // For guest, it's synchronous.
    if (platform === 'guest') {
        if (mountedRef.current) setIsAuthLoading(true); // Briefly show loading for guest setup
        const guestData = setUpGuestMode();
        setUserState(guestData);
        setGlobalGuestMode(true);
        if (mountedRef.current) setIsAuthLoading(false);
    } else {
        // For OAuth, LoginPage will handle the redirect by calling initiateOAuth.
        // UserContext.login might not be directly involved in starting the OAuth redirect.
        // If it were, we'd set isAuthLoading(true) and expect onAuthStateChanged to resolve it.
        console.log(`[UserContext] Login for ${platform} initiated (typically by LoginPage).`);
    }
  }, [setGlobalGuestMode]);

  const logout = useCallback(async () => {
    if (!mountedRef.current) return;
    console.log('[UserContext] Attempting logout.');
    setIsAuthLoading(true); 
    setAuthError(null);
    try {
      const auth = firebaseInstance.getAuthInstance();
      await signOut(auth); 
      localStorage.removeItem('guestUser'); 
      removeAuthToken(); 
      // onAuthStateChanged will set user to null and isAuthLoading to false.
      console.log('[UserContext] Firebase signOut successful. State update will occur via onAuthStateChanged listener.');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[UserContext] Error during logout:', error);
      setAuthError('Logout failed.');
      if (mountedRef.current) setIsAuthLoading(false);
    }
  }, [navigate]);

  const contextValue = useMemo(() => ({
    user,
    setUser: setUserState, 
    login,
    logout,
    error: authError,
    loading: isAuthLoading,
  }), [user, login, logout, authError, isAuthLoading]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};